import i18n from "i18next";
import Backend from "i18next-http-backend";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

// Define supported languages with their properties
export const languages = {
  ar: { name: "\u0627\u0644\u0639\u0631\u0628\u064A\u0629", dir: "rtl", flag: "\uD83C\uDDF8\uD83C\uDDE6" },
  en: { name: "English", dir: "ltr", flag: "\uD83C\uDDFA\uD83C\uDDF8" },
  fr: { name: "Fran\u00E7ais", dir: "ltr", flag: "\uD83C\uDDEB\uD83C\uDDF7" },
  de: { name: "Deutsch", dir: "ltr", flag: "\uD83C\uDDE9\uD83C\uDDEA" },
  es: { name: "Espa\u00F1ol", dir: "ltr", flag: "\uD83C\uDDEA\uD83C\uDDF8" },
} as const;

// List of language codes your application supports.
export const supportedLangs = Object.keys(languages) as Array<keyof typeof languages>;
export type SupportedLanguage = keyof typeof languages;

// Default language — fall back to English only so missing keys never surface
// another locale's string (e.g. "Frente" from Spanish appearing in the English UI).
const fallbackLng = "en";
const runtimeAssetBase = import.meta.env.BASE_URL;
// Locale files ship unhashed under /static/, so only a changed URL reaches a
// browser that already cached the previous copy. `__I18N_BUILD__` is injected
// by vite `define`; it is absent under vitest/node, hence the guard.
const localeCacheBust =
  typeof __I18N_BUILD__ === "undefined" ? "dev" : __I18N_BUILD__;
export const frontendNamespaces = [
  "analytics",
  "auth",
  "byok",
  "common",
  "community",
  "dashboard",
  "dataExport",
  "diet",
  "downloads",
  "errors",
  "experiments",
  "files",
  "health-assistant",
  "knowledge",
  "landing",
  "legal",
  "lifestyle",
  "medical",
  "notifications",
  "profile",
  "reminders",
  "seo",
  "store",
  "supplements",
  "symptoms",
  "treatments",
  "users",
  "waitlist",
  "pwa",
  "sync",
  "mobile",
] as const;
const globalI18nState = globalThis as typeof globalThis & {
  __PHD_I18N_INIT_PROMISE__?: Promise<unknown>;
  __PHD_I18N_LANGUAGE_SYNC_BOUND__?: boolean;
};

// Get stored language preference from localStorage
const getStoredLanguage = (): string | null => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("i18nextLng");
  }
  return null;
};

const getPathLanguage = (): SupportedLanguage | null => {
  if (typeof window === "undefined") return null;
  const firstSegment = window.location.pathname.split("/").filter(Boolean)[0];
  return supportedLangs.includes(firstSegment as SupportedLanguage)
    ? (firstSegment as SupportedLanguage)
    : null;
};

// Store language preference in localStorage
const storeLanguage = (lng: string): void => {
  if (typeof window !== "undefined") {
    localStorage.setItem("i18nextLng", lng);
    // Store language direction for CSS
    const dir = languages[lng as SupportedLanguage]?.dir || "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = lng;
    // Force style recalculation for RTL changes
    document.documentElement.style.direction = dir;
  }
};

// Initialize i18n
if (!i18n.isInitialized && !globalI18nState.__PHD_I18N_INIT_PROMISE__) {
  globalI18nState.__PHD_I18N_INIT_PROMISE__ = i18n
    // Load translation using HTTP -> see /public/locales
    .use(Backend)
    // Detect user language
    .use(LanguageDetector)
    // Pass the i18n instance to react-i18next
    .use(initReactI18next)
    // Initialize i18next
    .init({
    // Language detection options
    detection: {
      order: ["path", "localStorage", "navigator", "htmlTag"],
      caches: ["localStorage"],
      lookupFromPathIndex: 0,
      lookupLocalStorage: "i18nextLng",
    },

    // Fallback configuration
    fallbackLng: fallbackLng,

    // Only use base language codes (en, fr, etc.) without region (en-US, fr-FR)
    // This normalizes en-US -> en, fr-FR -> fr
    load: "languageOnly",

    // Supported languages (only base codes)
    supportedLngs: supportedLangs,

    // Clean language codes (en-US -> en)
    cleanCode: true,

    // Debug mode (disable in production)
    debug: process.env.NODE_ENV === "development",

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes
    },

    // Backend configuration
    backend: {
      loadPath: `${runtimeAssetBase}locales/{{lng}}/{{ns}}.json?v=${localeCacheBust}`,
    },

    // Namespace configuration
    ns: frontendNamespaces,
    defaultNS: "common",
      partialBundledLanguages: true,
    })
    .catch((error: unknown) => {
      globalI18nState.__PHD_I18N_INIT_PROMISE__ = undefined;
      throw error;
    });
}

// Listen for language changes and update localStorage/direction
if (!globalI18nState.__PHD_I18N_LANGUAGE_SYNC_BOUND__) {
  i18n.on("languageChanged", (lng) => {
    storeLanguage(lng);
  });
  globalI18nState.__PHD_I18N_LANGUAGE_SYNC_BOUND__ = true;
}

// Initialize language direction on first load
if (typeof window !== "undefined") {
  const currentLang = getPathLanguage() || i18n.language || getStoredLanguage() || "en";
  const dir = languages[currentLang as SupportedLanguage]?.dir || "ltr";
  document.documentElement.dir = dir;
  document.documentElement.lang = currentLang;
  // Force style recalculation for RTL changes
  document.documentElement.style.direction = dir;
}

// Export helper functions
export const changeLanguage = (lng: SupportedLanguage): Promise<any> => {
  return i18n.changeLanguage(lng);
};

export const getCurrentLanguage = (): SupportedLanguage => {
  return (i18n.language as SupportedLanguage) || "en";
};

export const isRTL = (lng?: string): boolean => {
  const lang = lng || i18n.language || "en";
  return languages[lang as SupportedLanguage]?.dir === "rtl";
};

export const getLanguageInfo = (lng?: string) => {
  const lang = lng || i18n.language || "en";
  return languages[lang as SupportedLanguage] || languages["en"];
};

export default i18n;
